import React from 'react';
import type { ExtraInfo } from '@/schema/schema';
import { invariant } from '@/util/invariant';

type ExtraInfoContextType = {
  state: ReadonlyMap<string, ExtraInfo>;
  add: (path: string, extraInfo: ExtraInfo) => void;
  remove: (path: string) => void;
};

const ExtraInfoContext = React.createContext<ExtraInfoContextType | null>(null);

export const ExtraInfoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = React.useState<ExtraInfoContextType['state']>(
    () => new Map(),
  );

  const value = React.useMemo<ExtraInfoContextType>(() => {
    function add(path: string, extra_info: ExtraInfo) {
      setState((state) => {
        const newState = new Map(state);

        if (extra_info == null) {
          newState.delete(path);
        } else {
          newState.set(path, extra_info);
        }

        return newState;
      });
    }

    function remove(path: string) {
      setState((state) => {
        if (!state.has(path)) {
          return state;
        }

        const newState = new Map(state);

        newState.delete(path);

        return newState;
      });
    }

    return { state, add, remove };
  }, [state]);

  return <ExtraInfoContext value={value}>{children}</ExtraInfoContext>;
};

function useExtraInfoContext() {
  const context = React.use(ExtraInfoContext);

  invariant(
    context,
    'useExtraInfoContext must be used within an ExtraInfoProvider',
  );

  return context;
}

export function useAdd() {
  const { add } = useExtraInfoContext();

  return add;
}

export function useRemove() {
  const { remove } = useExtraInfoContext();

  return remove;
}

export function useExtraInfos(path: string) {
  const { state } = useExtraInfoContext();

  return state.get(path);
}
